import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Stack
} from '@mui/material';
import { Add, Edit, Delete, Print, Search } from '@mui/icons-material';
import { collection, addDoc, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from 'sweetalert2';

function Staffoutpass() {
    const [outPasses, setOutPasses] = useState([]);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [printData, setPrintData] = useState(null);

    const [formData, setFormData] = useState({
        empName: '', 
        date: new Date().toLocaleDateString('en-GB'), 
        reason: '', 
        outTime: '', 
        inTime: '', 
        duration: '', 
        contactNo: '',
        passType: 'Personal', // Personal or Official
        returnStatus: 'Return' // Return or Not return
    });

    const triggerPrint = (item) => {
        setPrintData(item);
        setTimeout(() => { window.print(); }, 700);
    };

    // 1. Real-time Sync
    useEffect(() => {
        const q = query(collection(db, "staff_out_passes"), orderBy("srNo", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setOutPasses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    const handleSave = async () => {
        if(!formData.empName) return Swal.fire("Error", "Please enter Employee Name", "error");
        
        try {
            if (editId) {
                await updateDoc(doc(db, "staff_out_passes", editId), formData);
            } else {
                // Starting from 6671 as per your image
                const nextSrNo = outPasses.length > 0 ? (Math.max(...outPasses.map(o => o.srNo || 0)) + 1) : 1;
                await addDoc(collection(db, "staff_out_passes"), { ...formData, srNo: nextSrNo, timestamp: new Date() });
            }
            setOpen(false); setEditId(null); resetForm();
            Swal.fire({ title: "Saved!", timer: 1000, icon: "success", showConfirmButton: false });
        } catch (e) { Swal.fire("Error", e.message, "error"); }
    };

    const resetForm = () => setFormData({
        empName: '', date: new Date().toLocaleDateString('en-GB'), 
        reason: '', outTime: '', inTime: '', 
        duration: '', contactNo: '', passType: 'Personal', returnStatus: 'Return'
    });

    // FIX: Added optional chaining ?. to prevent toLowerCase error
    const filteredPasses = outPasses.filter(p => 
        p.empName?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box p={2} sx={{ bgcolor: '#f1f5f9', minHeight: '100vh' }}>
            <style>
                {`
                @media print {
                    body * { visibility: hidden; }
                    #printable-pass, #printable-pass * { visibility: visible; }
                    #printable-pass { position: absolute; left: 0; top: 0; width: 100%; display: block !important; }
                    @page { size: landscape; margin: 5mm; }
                }
                .pass-container {
                    width: 200mm; border: 2px solid #000; padding: 20px; margin: auto;
                    font-family: 'Arial', sans-serif; background: #fff; border-radius: 10px;
                }
                .dotted-line { border-bottom: 1px dotted #000; display: inline-block; padding: 0 5px; font-weight: bold; }
                .check-box { width: 22px; height: 22px; border: 1px solid #000; display: inline-block; vertical-align: middle; text-align: center; line-height: 20px; margin: 0 8px; font-weight: bold; }
                .marathi-footer { background: #212121; color: #fff; padding: 8px; border-radius: 25px; font-size: 12px; text-align: center; margin-top: 20px; }
                `}
            </style>

            <Box className="no-print">
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight="900" color="#b71c1c">STAFF OUT PASS MANAGEMENT</Typography>
                    <Box display="flex" gap={2}>
                        <TextField 
                            size="small" 
                            placeholder="Search Name..." 
                            value={search} 
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
                        />
                        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => { resetForm(); setOpen(true); }}>New Entry</Button>
                    </Box>
                </Stack>

                <TableContainer component={Paper} elevation={3}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#b71c1c' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#fff' }}>Sr.No</TableCell>
                                <TableCell sx={{ color: '#fff' }}>Employee Name</TableCell>
                                <TableCell sx={{ color: '#fff' }}>Date</TableCell>
                                <TableCell sx={{ color: '#fff' }}>Reason</TableCell>
                                <TableCell sx={{ color: '#fff' }}>Times</TableCell>
                                <TableCell align="right" sx={{ color: '#fff' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPasses.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{item.srNo}</TableCell>
                                    <TableCell><b>{item.empName}</b></TableCell>
                                    <TableCell>{item.date}</TableCell>
                                    <TableCell>{item.reason}</TableCell>
                                    <TableCell>{item.outTime} - {item.inTime}</TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => triggerPrint(item)}><Print /></IconButton>
                                        <IconButton onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }}><Edit /></IconButton>
                                        <IconButton color="error" onClick={() => deleteDoc(doc(db, "staff_out_passes", item.id))}><Delete /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#b71c1c', color: '#fff' }}>{editId ? "Update Employee Pass" : "Issue New Employee Pass"}</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 1 }}>
                        <Grid className="col-sm-6 mb-2" item xs={12}>
                            <TextField fullWidth label="Full Name of Employee" size="small" value={formData.empName} onChange={e => setFormData({...formData, empName: e.target.value})} />
                        </Grid>
                        <Grid className="col-sm-6 mb-2" item xs={6}>
                            <TextField select fullWidth size="small" label="Pass Type" value={formData.passType} onChange={e => setFormData({...formData, passType: e.target.value})}>
                                <MenuItem value="Personal">Personal</MenuItem>
                                <MenuItem value="Official">Official</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-6 mb-2" item xs={6}>
                            <TextField select fullWidth size="small" label="Return Status" value={formData.returnStatus} onChange={e => setFormData({...formData, returnStatus: e.target.value})}>
                                <MenuItem value="Return">Return</MenuItem>
                                <MenuItem value="Not return">Not Return</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-6 mb-2" item xs={12}><TextField fullWidth size="small" label="Reason for leaving" value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} /></Grid>
                        <Grid className="col-sm-6 mb-2" item xs={4}><TextField fullWidth size="small" label="Out Time" placeholder="e.g. 10:30 AM" value={formData.outTime} onChange={e => setFormData({...formData, outTime: e.target.value})} /></Grid>
                        <Grid className="col-sm-6 mb-2" item xs={4}><TextField fullWidth size="small" label="In Time" placeholder="e.g. 12:00 PM" value={formData.inTime} onChange={e => setFormData({...formData, inTime: e.target.value})} /></Grid>
                        <Grid className="col-sm-6 mb-2" item xs={4}><TextField fullWidth size="small" label="Approx Hrs" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} /></Grid>
                        <Grid className="col-sm-6 mb-2" item xs={12}><TextField fullWidth size="small" label="Contact No." value={formData.contactNo} onChange={e => setFormData({...formData, contactNo: e.target.value})} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleSave}>Save Pass</Button>
                </DialogActions>
            </Dialog>

            <div id="printable-pass" style={{ display: 'none' }}>
                {printData && (
                    <div className="pass-container"  style={{
                        width: '260mm',
                        minHeight: '148mm', // Half A4 size (A5 landscape) looks great for passes
                        padding: '10px',
                        backgroundColor: 'white',
                        margin: '0 auto'
                    }}>
                        <Box textAlign="center" borderBottom="1px solid #000" pb={1} mb={1}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 800 }}>ABHINAV FOUNDATION'S, KAVATHEMAHANKAL</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900 }}>MOHAN MALI INTERNATIONAL SCHOOL</Typography>
                            <Typography sx={{ fontSize: '11px' }}>Near Kavathe Mahankal Railway Station, Alkud (S), Kavathe Mahankal.</Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography sx={{ fontSize: '1.1rem' }}>No. : <span style={{ color: 'red', fontWeight: 'bold', fontSize: '1.5rem', marginLeft: '10px' }}>{printData.srNo}</span></Typography>
                            <Box border="2px solid #000" px={4} py={0.5} borderRadius="4px"><Typography fontWeight={900} variant="h6">OUT PASS FOR EMPLOYEE</Typography></Box>
                            <Typography sx={{ fontSize: '1.1rem' }}>Date :- <span className="dotted-line" style={{ minWidth: '120px' }}>{printData.date}</span></Typography>
                        </Box>

                        <Box sx={{ lineHeight: 3 }}>
                            <Typography sx={{ fontSize: '1.2rem' }}>• Name Of the Employee :- <span className="dotted-line" style={{ width: '75%' }}>{printData.empName.toUpperCase()}</span></Typography>
                            <Typography sx={{ fontSize: '1.2rem' }}>
                                • Permission is hereby granted for the employee to leave the school premises on (date) 
                                <span className="dotted-line" style={{ minWidth: '150px', marginLeft: '10px' }}>{printData.date}</span> , for reason : 
                                <span className="dotted-line" style={{ width: '45%', marginLeft: '10px' }}>{printData.reason}</span>
                            </Typography>
                            
                            <Box display="flex" gap={2} mt={1} sx={{ fontSize: '1.2rem' }}>
                                <Box>• Personal <div className="check-box">{printData.passType === 'Personal' ? '✓' : ''}</div></Box>
                                <Box>Official <div className="check-box">{printData.passType === 'Official' ? '✓' : ''}</div></Box>
                                <Box ml={2}>out time : <span className="dotted-line" style={{ minWidth: '80px' }}>{printData.outTime}</span></Box>
                                <Box>In time : <span className="dotted-line" style={{ minWidth: '80px' }}>{printData.inTime}</span></Box>
                                <Box>(Approximately Hrs). <span className="dotted-line" style={{ minWidth: '60px' }}>{printData.duration}</span></Box>
                            </Box>

                            <Box display="flex" gap={2} mt={1} sx={{ fontSize: '1.2rem' }}>
                                <Box>• Return <div className="check-box">{printData.returnStatus === 'Return' ? '✓' : ''}</div></Box>
                                <Box>Not return <div className="check-box">{printData.returnStatus !== 'Return' ? '✓' : ''}</div></Box>
                                <Box ml={2} flex={1}>Contact No.: <span className="dotted-line" style={{ width: '82%' }}>{printData.contactNo}</span></Box>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mt={7} px={2}>
                            <Box textAlign="center">
                                <Box sx={{ borderTop: '1px solid #000', width: '200px', mb: 1 }}></Box>
                                <Typography fontWeight={900}>Signature of Employee</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Box sx={{ borderTop: '1px solid #000', width: '200px', mb: 1 }}></Box>
                                <Typography fontWeight={900}>Approved by : Principal / Admin</Typography>
                            </Box>
                        </Box>

                        <div className="marathi-footer">
                            टीप : कर्मचाऱ्यांना मान्य केलेल्या वेळेपेक्षा अधिक वेळ लागणार असल्यास त्यांनी तात्काळ कार्यालयाशी संपर्क साधावा.
                        </div>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Staffoutpass;